/**
 * Local state for the (mock) Admin Settings save flow.
 *
 * Extracted from AdminSettingsPage.tsx (Sub-PR 6.3).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdminSettings } from './types'

const SAVE_DELAY_MS = 1000
const MESSAGE_DURATION_MS = 3000
const DEFAULT_SESSION_TIMEOUT = 60
const DEFAULT_MAX_LOGIN_ATTEMPTS = 5

const INITIAL_SETTINGS: AdminSettings = {
  allowSelfRegistration: false,
  requireEmailVerification: true,
  enableAuditLogging: true,
  sessionTimeoutMinutes: DEFAULT_SESSION_TIMEOUT,
  maxLoginAttempts: DEFAULT_MAX_LOGIN_ATTEMPTS,
  enforceStrongPasswords: true,
  enableTwoFactor: false,
  maintenanceMode: false,
}

interface UseAdminSettingsFormResult {
  settings: AdminSettings
  setSettings: (s: AdminSettings) => void
  saving: boolean
  savedMessage: string | null
  handleSave: () => Promise<void>
}

export function useAdminSettingsForm(): UseAdminSettingsFormResult {
  const { t } = useTranslation('admin')
  const [settings, setSettings] = useState<AdminSettings>(INITIAL_SETTINGS)
  const [saving, setSaving] = useState(false)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)

  const handleSave = async (): Promise<void> => {
    setSaving(true)
    await new Promise((resolve) => setTimeout(resolve, SAVE_DELAY_MS))
    setSaving(false)
    setSavedMessage(t('adminSettings.settingsSaved'))
    setTimeout(() => {
      setSavedMessage(null)
    }, MESSAGE_DURATION_MS)
  }

  return { settings, setSettings, saving, savedMessage, handleSave }
}
